import { useState } from 'react';
import type { LearningOverview, Report, ReelOutcome } from '@reelmachine/core';
import { useLearningOverview, useReport, useReports } from '../../api/learning';
import { formatObjective, formatPercent, formatServed, formatUsd } from '../../lib/learning-format';
import { Markdown } from '../../lib/markdown';
import { useIsMock } from '../../ui/mockFlag';
import { QueryState } from './QueryState';

const ACTION_LABEL: Record<Report['actions'][number]['kind'], string> = {
  applied: 'Applied',
  experiment_started: 'Experiment started',
  experiment_ended: 'Experiment ended',
  decision_created: 'Decision created',
  rolled_back: 'Rolled back',
  none: 'No action',
};

export function Kpis({ recent }: { recent: LearningOverview['recent'] }) {
  const edit = recent.meanEditRatio;
  const items: Array<[string, string, string?]> = [
    ['Reels (28 days)', String(recent.reels)],
    ['Reach vs channel', formatObjective(recent.meanObjective)],
    [
      'Editors changed',
      formatPercent(edit),
      edit === undefined ? undefined : `Editors changed ${formatPercent(edit)} of the text`,
    ],
    ['Fidelity passed', formatPercent(recent.fidelityPassRate)],
  ];
  return (
    <dl className="kpis" style={{ margin: 0 }}>
      {items.map(([label, value, title]) => (
        <div className="kpi" key={label} title={title}>
          <dt className="label">{label}</dt>
          <dd style={{ margin: 0 }}>
            <strong>{value}</strong>
            {label === 'Editors changed' && <span className="nums"> of the text</span>}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/** Recent posted reels with the playbook version they were served. */
export function Outcomes({ outcomes }: { outcomes: ReelOutcome[] }) {
  if (!outcomes.length) return null;
  return (
    <details className="card">
      <summary className="label">Recent reels ({outcomes.length})</summary>
      <div className="md-table">
        <table className="arms" aria-label="Recent reels">
          <thead>
            <tr>
              <th scope="col">Posted</th>
              <th scope="col">Playbook</th>
              <th scope="col">Reach vs channel</th>
            </tr>
          </thead>
          <tbody>
            {outcomes.map((o) => (
              <tr key={o.reelId}>
                <th scope="row">{o.postedAt.slice(0, 10)}</th>
                <td>
                  {formatServed(o.playbook)}
                  {o.playbook?.arm ? ` · ${o.playbook.arm}` : ''}
                </td>
                <td>{formatObjective(o.metrics?.objective)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  );
}

function ReportView({ id }: { id: string }) {
  const q = useReport(id);
  const mock = useIsMock();
  return (
    <QueryState query={q}>
      {(report) => (
        <article className="card" aria-label={report.title}>
          <div className="row between">
            <h3>{report.title}</h3>
            <span className="nums">{report.date}</span>
          </div>
          {mock && <span className="chip warn">Example data</span>}
          {report.actions.length > 0 && (
            <ul className="row" aria-label="Actions" style={{ listStyle: 'none', padding: 0, gap: 6 }}>
              {report.actions.map((a, i) => (
                <li key={i} className={`chip ${a.kind === 'none' ? '' : 'rec'}`} title={a.summary}>
                  {ACTION_LABEL[a.kind]}
                  {a.ref ? `: ${a.ref}` : ''}
                </li>
              ))}
            </ul>
          )}
          <Markdown source={report.body} />
          <p className="nums">
            Run cost {formatUsd(report.costUsd)} · {report.model}
          </p>
        </article>
      )}
    </QueryState>
  );
}

export function ReportTab() {
  const overview = useLearningOverview();
  const reports = useReports();
  const [picked, setPicked] = useState<string | null>(null);
  const current = picked ?? overview.data?.latestReport?.id ?? reports.data?.[0]?.id;
  return (
    <>
      <QueryState query={overview}>
        {(o) => (
          <>
            <Kpis recent={o.recent} />
            <Outcomes outcomes={o.outcomes} />
          </>
        )}
      </QueryState>
      {current ? (
        <ReportView id={current} />
      ) : (
        <p className="muted">No report yet. The improver writes one every morning.</p>
      )}
      {reports.data && reports.data.length > 1 && (
        <section aria-label="All reports" className="field">
          <span className="label">All reports</span>
          <ul className="plain-list">
            {reports.data.map((r) => (
              <li key={r.id}>
                <button
                  type="button"
                  className="linkrow"
                  aria-current={r.id === current ? 'true' : undefined}
                  onClick={() => setPicked(r.id)}
                >
                  <span>{r.title}</span>
                  <span className="nums">{r.date}</span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  );
}
